import { useState } from "react"
import { Button } from "@/components/ui/button"
import { MesAnoInput } from "@/components/form/input/date-mask-input"
import { useCreateCompetenciaRestrita } from "@/features/products/services"
import { useToast } from "@/lib/hooks/use-toast"

type ButtonCompetenciaProps = {
  onCreate?: (params: { start: string; end: string }) => void | Promise<void>
  isLoading?: boolean
  id_apuracao?: string
}

export default function ButtonCompetencia({
  id_apuracao,
  onCreate,
  isLoading = false,
}: ButtonCompetenciaProps) {
  const { toast } = useToast()
  const [periodoInicial, setPeriodoInicial] = useState("")
  const [periodoFinal, setPeriodoFinal] = useState("")
  const [requestError, setRequestError] = useState<string | null>(null)

  const createCompetencia = useCreateCompetenciaRestrita()

  const canSubmit =
    Boolean(periodoInicial && periodoFinal && id_apuracao) &&
    !isLoading &&
    !createCompetencia.isPending

  const normalizePeriodo = (value: string): string => {
    const cleaned = value.replace(/\D/g, "")
    if (cleaned.length !== 6) {
      throw new Error("Informe o período no formato MM/AAAA.")
    }

    const month = cleaned.slice(0, 2)
    const year = cleaned.slice(2)
    const monthAsNumber = Number(month)
    if (!Number.isInteger(monthAsNumber) || monthAsNumber < 1 || monthAsNumber > 12) {
      throw new Error("Mês inválido. Use um valor entre 01 e 12.")
    }

    return `${month}/${year}`
  }

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error && error.message) {
      return error.message
    }
    return "Erro ao criar competência."
  }

  const handleCreate = async () => {
    if (!canSubmit) return

    try {
      setRequestError(null)

      const normalizedPeriodoInicial = normalizePeriodo(periodoInicial)
      const normalizedPeriodoFinal = normalizePeriodo(periodoFinal)

      await createCompetencia.mutateAsync({
        idApuracao: id_apuracao!,
        periodoInicial: normalizedPeriodoInicial,
        periodoFinal: normalizedPeriodoFinal,
      })

      toast({ description: "Competência criada com sucesso." })
      await onCreate?.({ start: periodoInicial, end: periodoFinal })
    } catch (error) {
      const message = getErrorMessage(error)
      setRequestError(message)
      toast({ variant: "destructive", description: message })
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Periodo Inicial</label>
          <MesAnoInput value={periodoInicial} onChange={setPeriodoInicial} />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Periodo Final</label>
          <MesAnoInput value={periodoFinal} onChange={setPeriodoFinal} />
        </div>

        <div className="flex items-end">
          <Button className="w-full" onClick={handleCreate} disabled={!canSubmit}>
            {isLoading || createCompetencia.isPending ? "Criando..." : "Criar competencia"}
          </Button>
        </div>
      </div>
      {requestError && (
        <p className="mt-3 text-xs text-destructive">{requestError}</p>
      )}
    </div>
  )
}
